import React, { useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Typography,
  Switch,
  Button,
} from '@material-ui/core'
import { makeStyles as makeStylesHook } from '@material-ui/styles'
import { PageHeader } from 'components/page-header'
import ContentSection from 'components/content-section'
import ActionBar from 'components/form-util/action-bar'
import CtrlTextField from 'components/form-util/ctrl-text-field'
import CtrlCheckbox from 'components/form-util/ctrl-checkbox'
import { SettingsFormControlLabel } from '../booking-url/styles'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import {
  LEAD_REPLY_PREFERENCES_QUERY,
  UPDATE_LEAD_REPLY_PREFERENCES,
} from '../queries'

const useStyles = makeStylesHook(theme => ({
  root: {
    padding: theme.spacing(7),
    [theme.breakpoints.only('xs')]: {
      padding: '16px 16px 96px 16px',
    },
  },
  section: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
}))

const validationSchema = yup.object().shape({
  enabled: yup.boolean(),
  message: yup
    .string()
    .max(1000, 'Message must be at most 1000 characters')
    .required('Message is required'),
  includeBookingLink: yup.boolean(),
})

const LeadsAutoReplySettings = () => {
  const classes = useStyles()
  const { path } = useRouteMatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { data, loading, error } = useQuery(LEAD_REPLY_PREFERENCES_QUERY)
  const [updatePreferences, { loading: saving }] = useMutation(
    UPDATE_LEAD_REPLY_PREFERENCES,
  )

  const preferences = data?.currentUser?.attributes?.preferences || {}

  // Initial values from API
  const original = useRef({
    enabled: preferences.leadAutoReplyEnabled || false,
    message: preferences.leadAutoReplyMessage || '',
    includeBookingLink: preferences.leadAutoReplyIncludeBookingLink || false,
  })

  const form = useForm({
    defaultValues: original.current,
    resolver: yupResolver(validationSchema),
  })
  const { control, handleSubmit, reset, formState } = form
  const { isDirty } = formState

  // Sync form with API data
  useEffect(() => {
    const newVals = {
      enabled: preferences.leadAutoReplyEnabled || false,
      message: preferences.leadAutoReplyMessage || '',
      includeBookingLink: preferences.leadAutoReplyIncludeBookingLink || false,
    }
    original.current = newVals
    reset(newVals)
  }, [
    preferences.leadAutoReplyEnabled,
    preferences.leadAutoReplyMessage,
    preferences.leadAutoReplyIncludeBookingLink,
    reset,
  ])

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const onSubmit = async values => {
    try {
      await updatePreferences({
        variables: {
          input: {
            leadAutoReplyEnabled: values.enabled,
            leadAutoReplyMessage: values.message,
            leadAutoReplyIncludeBookingLink: values.includeBookingLink,
          },
        },
      })
      snackbarSuccess('Auto-reply settings saved!')
      history.goBack()
    } catch (e) {
      snackbarError(e.message)
    }
  }

  if (loading) return <LinearProgress color="primary" />
  if (error) {
    return <Typography color="error">Error loading settings.</Typography>
  }

  return (
    <>
      <PageHeader title="Leads Auto-Reply" crumbs={crumbs} />
      <ActionBar>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={() => history.goBack()}
            variant="text"
            style={{ marginRight: 16 }}
            color="primary"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            color="primary"
            variant="contained"
            disabled={saving || !isDirty}
          >
            Save
          </Button>
        </div>
      </ActionBar>
      <Container className={classes.root} style={{ position: 'relative' }}>
        <FormProvider {...form}>
          <form>
            <ContentSection
              title="Leads Auto-Reply Message"
              className={classes.section}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Customize the message that will be automatically added to
                    the confirmation email golfers receive after submitting a
                    lead. Use it to thank them, share next steps, or include a
                    booking link.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="enabled"
                    control={control}
                    render={({ value, onChange }) => (
                      <SettingsFormControlLabel
                        control={
                          <Switch
                            checked={!!value}
                            onChange={e => onChange(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body1">
                            Send Automatically
                          </Typography>
                        }
                        style={{ marginBottom: 8 }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CtrlTextField
                    name="message"
                    control={control}
                    errors={form.formState.errors}
                    label="Auto-Reply Message"
                    multiline
                    minRows={5}
                    fullWidth
                    variant="outlined"
                    required
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    When checked, your message will include a link to your "Book
                    Now" page — whether it's your PGA Coach booking calendar or
                    an external site, depending on how you've configured your
                    'Book Now Preferences.'
                  </Typography>
                  <Box mt={1}>
                    <CtrlCheckbox
                      name="includeBookingLink"
                      label="Include Booking Link"
                      control={control}
                      errors={form.formState.errors}
                    />
                  </Box>
                </Grid>
              </Grid>
            </ContentSection>
          </form>
        </FormProvider>
      </Container>
    </>
  )
}

export default LeadsAutoReplySettings
